import React, { useMemo, useCallback } from 'react'
import { RootState, useSelector } from 'src/state/redux'
import { DNAButton, DNAChip } from '@alucio/lux-ui'
import { useDNAFolderNav } from 'src/components/DNA/Folder/Nav/DNAFolderNav'
import { useDNAFolderActions } from 'src/components/DNA/Folder/DNAFolder.actions'
import { useAppSettings } from 'src/state/context/AppSettings'
import { userById } from 'src/state/redux/selector/user'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import format from 'date-fns/format'
import { TitleBar, useCurrentPage } from 'src/components/DNA'
import CustomDeckButton from 'src/components/Header/CustomDeckButton/CustomDeckButton'
import { FolderActionsUi2, useFolderOperations } from './FolderActions'
import { useDNAMyFoldersSharedResources } from './DNAMyFolders'
import { isFolderORM } from 'src/types/typeguards'

// Types for the button configuration
interface ButtonConfig {
  buttons: React.ReactNode[];
}

/**
 * Custom hook that determines whether the shared button should be visible
 *
 * This hook implements complex business logic to control when the "Share" button
 * appears in the folder interface. The visibility depends on several factors:
 * based on https://github.com/alucioinc/eeb/blob/35ffe5b16e51a2947ae64bce5fd1205ad7c5bcdd/src/applications/beacon/src/components/InfoPanel/State/InfoPanelStateProvider.tsx#L49
 *
 * 1. **Basic Requirements**: User must be online AND inside a folder
 * 2. **Feature Flag Control**: The 'enableSharedFolders' feature flag controls sharing behavior
 * 3. **Permission Logic**: Different behavior for folder owners vs. recipients
 *
 * @returns {boolean} True if the share button should be visible, false otherwise
 *
 * @example
 * ```tsx
 * const showShareButton = useSharedButtonVisibility();
 *
 * return showShareButton ? (
 *   <DNAButton onPress={handleShare}>Share</DNAButton>
 * ) : null;
 * ```
 */
const useSharedButtonVisibility = () => {
  const { isOnline } = useAppSettings();
  const folderNav = useDNAFolderNav()
  const isSubFolder = useMemo(() => folderNav.folderStack.length > 1, [folderNav.folderStack])
  const currentFolder = useMemo(
    () => folderNav.folderStack.slice(-1)[0],
    [folderNav.folderStack],
  )
  const selector = useCallback((state: RootState) => state.infoPanel, [])
  const isInAFolder = !!currentFolder?.model?.id
  const isShareButtonVisible = isInAFolder && isOnline && !isSubFolder
  const globalInfoPanelState = useSelector(selector)

  // Check if the currently active item in info panel is a folder shared with the user
  // This helps distinguish between folder owners and recipients
  const isSharedWithTheUser = (
    isFolderORM(globalInfoPanelState.activeItemORM) &&
    globalInfoPanelState.activeItemORM.meta.isSharedWithTheUser
  )

  const enableSharedFolders = useFeatureFlags('enableSharedFolders')

  // Final visibility logic with two scenarios:
  //
  // Scenario 1: Feature flag is DISABLED (!enableSharedFolders)
  //   - Show button for all users when basic requirements are met
  //   - This allows sharing even when the feature is turned off
  //
  // Scenario 2: Feature flag is ENABLED (enableSharedFolders)
  //   - Only show button for users who have the folder shared with them
  //   - This restricts sharing to only recipients, not owners
  //
  // The double negation (!!) ensures we return a boolean value
  return isShareButtonVisible
    ? !(isSharedWithTheUser || !enableSharedFolders)
    : false
}

/**
 * Custom hook that creates button configuration based on current folder state
 */
const useButtonConfig = () => {
  const { deviceMode } = useAppSettings();
  const isDesktop = deviceMode === 'desktop'
  const folderNav = useDNAFolderNav()
  const folderActions = useDNAFolderActions()
  const { canCreateSubFolder, canCreateFolder } = useFolderOperations()
  const route = useCurrentPage({ exact: false })

  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const archivedFoldersTab = !!route?.PATH.includes('archived_folders')
  const isCustomDeckUsageEnabled = useFeatureFlags('BEAC_6655_custom_deck_usage')

  const currentFolder = useMemo(() => folderNav.folderStack.slice(-1)[0], [folderNav.folderStack])
  const folderButtons = FolderActionsUi2() || []
  const isShareButtonVisible = useSharedButtonVisibility()

  return useMemo((): Record<'myFolders' | 'insideFolder', ButtonConfig> => ({
    myFolders: {
      buttons: [
        !isPublisherRoute && !archivedFoldersTab && isCustomDeckUsageEnabled && (
          <CustomDeckButton
            key="custom-deck"
            mode="myFolders"
            appearance="outline"
            status="primary"
            size={isDesktop ? 'md' : 'xl'}
          />
        ),
        // Create folder button
        canCreateFolder && (
          <DNAButton
            key="create-folder"
            onPress={() => folderActions.createFolder(currentFolder)()}
            size={isDesktop ? 'md' : 'xl'}
          >
            Create folder
          </DNAButton>
        ),
      ].filter(Boolean),
    },
    insideFolder: {
      buttons: [
        (isShareButtonVisible ? <DNAButton
          key="Share"
          appearance="outline"
          status="primary"
          onPress={() => folderActions.share(currentFolder)()}
          size={isDesktop ? 'md' : 'xl'}
        >
          Share folder
        </DNAButton> : null),
        ...folderButtons,
        !isPublisherRoute && !archivedFoldersTab && isCustomDeckUsageEnabled && (
          <CustomDeckButton
            key="custom-deck"
            mode="myFolders"
            appearance="filled"
            status="primary"
            size={isDesktop ? 'md' : 'xl'}
          />
        ),
      ].filter(Boolean),
    },
  }), [
    isPublisherRoute,
    archivedFoldersTab,
    isCustomDeckUsageEnabled,
    canCreateFolder,
    canCreateSubFolder,
    currentFolder,
    folderActions,
    isShareButtonVisible,
  ])
}

/**
 * Custom hook that contains all the logic for FolderInfoHeader component
 */
const useFolderInfoHeader = () => {
  const folderNav = useDNAFolderNav()
  const { toggleDisplayMode, displayMode } = useDNAMyFoldersSharedResources();
  const route = useCurrentPage({ exact: false })
  const sharedFoldersTab = !!route?.PATH.includes('shared_folders');
  const defaultTitle = sharedFoldersTab ? 'Shared With Me' : 'My Folders';
  const defaultDescription = sharedFoldersTab
    ? 'Manage your shared folders'
    : 'Manage your content and meetings with folders';

  // Get current folder and user info
  const currentFolder = useMemo(() => folderNav.folderStack.slice(-1)[0], [folderNav.folderStack]);
  const createdByUserId = useMemo(() => currentFolder?.model?.createdBy, [currentFolder?.model?.createdBy]);
  const createdByUser = useSelector((state) => userById(state, createdByUserId));
  const createdBy = useMemo(() =>
    createdByUser?.meta?.formattedName || createdByUserId || 'Unknown',
  [createdByUser?.meta?.formattedName, createdByUserId],
  );

  // TODO: Check why currentFolder?.relations?.parentFolderORM is always null  const isShareButtonVisible = currentFolder?.relations?.parentFolderORM && isOnline
  const isInAFolder = !!currentFolder?.model?.id

  // Use the new button configuration hook
  const buttonConfig = useButtonConfig();

  const getCurrentOption = useCallback(() => {
    if (isInAFolder) return buttonConfig.insideFolder;
    return buttonConfig.myFolders;
  }, [buttonConfig, isInAFolder]);

  const currentOption = getCurrentOption();

  const getFolderDescription = useCallback(() => {
    if (!currentFolder?.model) {
      return defaultDescription;
    }

    const createdAt = currentFolder.model.createdAt
      ? format(new Date(currentFolder.model.createdAt), 'MMM dd, yyyy, h:mm a')
      : 'Unknown date';
    const updatedAt = currentFolder.model.updatedAt
      ? format(new Date(currentFolder.model.updatedAt), 'MMM dd, yyyy, h:mm a')
      : 'Unknown date';

    return `Created by ${createdBy} on ${createdAt} | Last updated: ${updatedAt}`;
  }, [currentFolder?.model, createdBy, defaultDescription]);

  const getSharedBadge = useCallback(() => {
    const isShared = currentFolder?.model.shareStatus === 'IS_SHARED';
    return isShared ? (
      <DNAChip appearance="subtle" status="secondary" size="sm" rounded>SHARED</DNAChip>
    ) : null;
  }, [currentFolder?.model.shareStatus]);

  const title = useMemo(() =>
    currentFolder?.model.name || defaultTitle,
  [currentFolder?.model.name, defaultTitle],
  );

  return {
    title,
    description: getFolderDescription(),
    badge: getSharedBadge(),
    displayMode,
    toggleDisplayMode,
    currentOption,
  };
};

/**
 * Component that displays folder information in a TitleBar
 * Shows folder name, creation/update details, and shared status
 */
const FolderInfoHeader: React.FC = () => {
  const {
    title,
    description,
    badge,
    displayMode,
    toggleDisplayMode,
    currentOption,
  } = useFolderInfoHeader();

  return (
    <TitleBar
      title={title}
      description={description}
      badge={badge}
      showToggleView={true}
      toggleView={{
        activeView: displayMode,
        onToggle: toggleDisplayMode,
      }}
    >
      {currentOption.buttons.map((button, index) => (
        <TitleBar.Button key={index}>
          {button}
        </TitleBar.Button>
      ))}
    </TitleBar>
  );
};

export default FolderInfoHeader;
